import { 
  Box, 
  Typography, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useState } from 'react';

export function AgentNodeFields({ localNodeData, updateNodeData }) {
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);

  // Modo avanzado para almacenar respuesta completa
  const AdvanceResponse = localNodeData.AdvanceResponse || false;
  // Nuevo: modo para solo extraer de agente (solo OutputKey)
  const ExtractFromAgent = localNodeData.ExtractFromAgent || false;

  const handleOpenInstructionsModal = () => {
    if (localNodeData.instructions) {
      setIsInstructionsModalOpen(true);
    }
  };

  const handleCloseInstructionsModal = () => {
    setIsInstructionsModalOpen(false);
  };

  // Handlers para switches, asegurando que sean mutuamente excluyentes
  const handleToggleAdvanceResponse = (checked) => {
    updateNodeData('AdvanceResponse', checked);
    if (checked) {
      // desactivar el otro modo
      updateNodeData('ExtractFromAgent', false);
    }
  };

  const handleToggleExtractFromAgent = (checked) => {
    updateNodeData('ExtractFromAgent', checked);
    if (checked) {
      // desactivar el otro modo
      updateNodeData('AdvanceResponse', false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Instrucciones
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <TextField
            value={localNodeData.instructions || ''}
            multiline
            rows={4}
            fullWidth
            placeholder="Eres un asistente..."
            onChange={(e) => updateNodeData('instructions', e.target.value)}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 4,
              bottom: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Button
              size="small"
              onClick={handleOpenInstructionsModal}
              disabled={!localNodeData.instructions}
              aria-label="Ver instrucciones completas"
              sx={{ minWidth: 'auto', padding: 0, minHeight: 0 }}
            >
              <FullscreenIcon fontSize="small" />
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localNodeData.includeChatHistory || false}
              onChange={(e) => updateNodeData('includeChatHistory', e.target.checked)}
            />
          }
          label="Incluye historial del chat"
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="model-select-label">Model</InputLabel>
          <Select
            labelId="model-select-label"
            label="Model"
            onChange={(e) => updateNodeData('model', e.target.value)}
            defaultValue={localNodeData.model || 'gpt-5.4'}
          >
            <MenuItem value="gpt-5.4">gpt-5.4</MenuItem>
            <MenuItem value="gpt-5.2">gpt-5.2</MenuItem>
            <MenuItem value="gpt-5.1">gpt-5.1</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="reasoning-select-label">Reasoning effort</InputLabel>
          <Select
            labelId="reasoning-select-label"
            value={localNodeData.reasoningEffort || 'low'}
            label="Reasoning effort"
            onChange={(e) => updateNodeData('reasoningEffort', e.target.value)}
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Respuesta esperada
        </Typography>

        {/* Controles de modo: solo se puede activar uno */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={AdvanceResponse}
                onChange={(e) => handleToggleAdvanceResponse(e.target.checked)}
              />
            }
            label="Almacenar respuesta"
          />

          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={ExtractFromAgent}
                onChange={(e) => handleToggleExtractFromAgent(e.target.checked)}
              />
            }
            label="Extraer de Agente"
          />
        </Box>

        {/* MODO SIMPLE: ninguno de los dos switches está activo */}
        {!AdvanceResponse && !ExtractFromAgent && (
          <TextField
            value={localNodeData.expected || ''}
            size="small"
            fullWidth
            multiline
            rows={2}
            onChange={(e) => updateNodeData('expected', e.target.value)}
          />
        )}

        {/* MODO AVANZADO 1: Almacenar respuesta (OutputKey + expected) */}
        {AdvanceResponse && !ExtractFromAgent && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label="output_key"
              placeholder="Ej: respuesta_agente"
              value={localNodeData.OutputKey || ''}
              size="small"
              fullWidth
              onChange={(e) => updateNodeData('OutputKey', e.target.value)}
            />
            <TextField
              label="Formato / valor esperado"
              placeholder="Ej: resumen, listado de tareas, etc."
              value={localNodeData.expected || ''}
              size="small"
              fullWidth
              multiline
              rows={2}
              onChange={(e) => updateNodeData('expected', e.target.value)}
            />
          </Box>
        )}

        {/* MODO AVANZADO 2: Extraer de Agente (solo OutputKey) */}
        {ExtractFromAgent && !AdvanceResponse && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label="output_key"
              placeholder="Ej: respuesta_agente"
              value={localNodeData.OutputKey || ''}
              size="small"
              fullWidth
              onChange={(e) => updateNodeData('OutputKey', e.target.value)}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={isInstructionsModalOpen}
        onClose={handleCloseInstructionsModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Instrucciones completas</DialogTitle>
        <DialogContent dividers>
          <TextField
            value={localNodeData.instructions || ''}
            size="small"
            fullWidth
            multiline
            rows={12}
            onChange={(e) => updateNodeData('instructions', e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstructionsModal} variant="contained" color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}